// src/components/purchase/RegistrarStockModal.js
// Modal para registrar stock asociado a una factura de compra

import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Button, Table, Spinner, InputGroup } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { createStockReceipt } from '../../services/stockApi';
import { listPurchases } from '../../services/purchaseApi';
import { dotoniwoofApi } from '../../api/dotoniwooApi';
import './RegistrarStockModal.css';

const formatterCOP = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const todayYYYYMMDD = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/** Nombre del item con atributos (peso, color, talla). Omite los que son N/A. Ej: "Chunky 1Kg" */
const getItemDisplayName = (item) => {
  if (!item) return '';
  const base = item.description || item.product?.name || '';
  const attrs = [];
  const w = item.weight?.weight_name;
  if (w && String(w).toUpperCase() !== 'N/A') attrs.push(w);
  const c = item.color?.color_name;
  if (c && String(c).toUpperCase() !== 'N/A') attrs.push(c);
  const s = item.size?.size_name;
  if (s && String(s).toUpperCase() !== 'N/A') attrs.push(s);
  return attrs.length > 0 ? `${base} ${attrs.join(' ')}` : base;
};

const ItemImageThumb = ({ url, alt }) => {
  const [hasError, setHasError] = useState(false);
  const showImage = url && typeof url === 'string' && url.trim() && !hasError;
  return (
    <div className="stock-item-image-wrap">
      {showImage ? (
        <img src={url} alt={alt || ''} className="stock-item-img" onError={() => setHasError(true)} />
      ) : (
        <div className="stock-item-image-placeholder">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" fill="currentColor" opacity="0.5"/>
          </svg>
        </div>
      )}
    </div>
  );
};

const RegistrarStockModal = ({ show, onHide, purchaseId: initialPurchaseId, onSuccess }) => {
  const { user } = useAuth();
  const [idPurchase, setIdPurchase] = useState(initialPurchaseId || '');
  const [receiptDate, setReceiptDate] = useState(todayYYYYMMDD());
  const [items, setItems] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [itemsCatalog, setItemsCatalog] = useState([]);
  const [searchItem, setSearchItem] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [selectedItem, setSelectedItem] = useState(null);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [newQty, setNewQty] = useState(1);
  const [newUnitCost, setNewUnitCost] = useState('');
  const [newDiscount, setNewDiscount] = useState(0);

  const resetForm = () => {
    setItems([]);
    setSelectedItem(null);
    setShowQuickAdd(false);
    setNewQty(1);
    setNewUnitCost('');
    setNewDiscount(0);
    setErrorMsg('');
    setSuccessMsg('');
    setReceiptDate(todayYYYYMMDD());
  };

  const handleSelectItem = (it) => {
    setSelectedItem(it);
    setNewQty(1);
    setNewUnitCost(it?.price ? String(it.price) : '');
    setNewDiscount(0);
    setShowQuickAdd(true);
    setErrorMsg('');
  };

  const closeQuickAdd = () => {
    setShowQuickAdd(false);
    setSelectedItem(null);
    setNewQty(1);
    setNewUnitCost('');
    setNewDiscount(0);
  };

  useEffect(() => {
    if (show) {
      setIdPurchase(initialPurchaseId || '');
      resetForm();
      const loadPurchases = async () => {
        const { ok, items: data } = await listPurchases(1, 100);
        if (ok) setPurchases(data || []);
      };
      const loadItems = async () => {
        try {
          const { data } = await dotoniwoofApi.get('/item.php');
          if (data?.code === 200) setItemsCatalog(data.message || []);
        } catch (e) {
          console.error(e);
        }
      };
      loadPurchases();
      loadItems();
    }
  }, [show, initialPurchaseId]);

  const filteredCatalog = useMemo(() => {
    if (!searchItem.trim()) return itemsCatalog.slice(0, 48);
    const term = searchItem.toLowerCase();
    return itemsCatalog
      .filter(
        (it) =>
          (it.description || '').toLowerCase().includes(term) ||
          (it.product?.name || '').toLowerCase().includes(term) ||
          (it.sku || it.product?.sku || '').toLowerCase().includes(term)
      )
      .slice(0, 48);
  }, [itemsCatalog, searchItem]);

  const calcFinalPricePerUnit = (unitCost, rate) => {
    const cost = parseFloat(unitCost) || 0;
    const r = parseFloat(rate) || 0;
    return cost * (1 + r);
  };

  const addItem = () => {
    if (!selectedItem) {
      setErrorMsg('Selecciona un ítem.');
      return;
    }
    const qty = parseInt(newQty, 10);
    const cost = parseFloat(newUnitCost);
    const discount = parseFloat(newDiscount) || 0;
    if (!qty || qty < 1) {
      setErrorMsg('La cantidad debe ser al menos 1.');
      return;
    }
    if (isNaN(cost) || cost <= 0) {
      setErrorMsg('El costo unitario debe ser mayor que 0.');
      return;
    }
    const rate = parseFloat(selectedItem.rate) || 0;
    const finalPrice = calcFinalPricePerUnit(cost, rate);
    setItems((prev) => [
      ...prev,
      {
        key: Date.now(),
        id_item: selectedItem.id_item,
        quantity: qty,
        unit_cost: cost,
        discount_amount: discount,
        price: finalPrice,
        rate,
        _display: selectedItem
      }
    ]);
    closeQuickAdd();
    setErrorMsg('');
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');
    if (!user?.uid) {
      setErrorMsg('Sesión no válida.');
      return;
    }
    if (!idPurchase) {
      setErrorMsg('Selecciona una factura de compra.');
      return;
    }
    if (items.length === 0) {
      setErrorMsg('Agrega al menos un ítem.');
      return;
    }
    const payload = {
      firebase_UID: user.uid,
      id_purchase: parseInt(idPurchase, 10),
      receipt_date: receiptDate,
      items: items.map((it) => ({
        id_item: it.id_item,
        quantity: it.quantity,
        unit_cost: it.unit_cost,
        discount_amount: it.discount_amount || 0,
        price: it.price
      }))
    };
    try {
      setLoading(true);
      const { ok, status, data } = await createStockReceipt(payload);
      if (ok && (status === 201 || data?.code === 201)) {
        setSuccessMsg(data?.message || 'Stock registrado con éxito.');
        resetForm();
        onSuccess?.(idPurchase);
        setTimeout(() => onHide(), 1200);
      } else {
        setErrorMsg(data?.message || `Error al registrar (${status})`);
      }
    } catch (e) {
      setErrorMsg('Error inesperado al enviar.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" className="registrar-stock-modal" backdrop="static">
      <Modal.Header className="registrar-stock-modal-header">
        <Modal.Title>Registrar stock</Modal.Title>
        <button type="button" className="btn-close btn-close-white" aria-label="Cerrar" onClick={handleClose} />
      </Modal.Header>
      <Modal.Body className="registrar-stock-modal-body">
        {errorMsg && <div className="registrar-stock-error">{errorMsg}</div>}
        {successMsg && <div className="registrar-stock-success">{successMsg}</div>}

        <Form onSubmit={handleSubmit}>
          <div className="registrar-stock-row">
            <div className="registrar-stock-field">
              <Form.Label>Factura de compra</Form.Label>
              <Form.Select value={idPurchase} onChange={(e) => setIdPurchase(e.target.value)} required disabled={!!initialPurchaseId}>
                <option value="">Seleccionar</option>
                {purchases.map((p) => (
                  <option key={p.id_purchase} value={p.id_purchase}>
                    #{p.id_purchase} · {p.purchase_date} · {formatterCOP.format(p.grand_total ?? 0)}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="registrar-stock-field">
              <Form.Label>Fecha recepción</Form.Label>
              <Form.Control type="date" value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)} />
            </div>
          </div>

          <div className="registrar-stock-add">
            <div className="registrar-stock-search-wrap">
              <InputGroup>
                <InputGroup.Text className="registrar-stock-search-icon">
                  <i className="fas fa-search" />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Buscar por SKU, descripción o nombre..."
                  value={searchItem}
                  onChange={(e) => setSearchItem(e.target.value)}
                  className="registrar-stock-search"
                />
              </InputGroup>
            </div>
            <div className="registrar-stock-grid">
              {filteredCatalog.length === 0 ? (
                <div className="registrar-stock-empty-catalog">
                  {itemsCatalog.length === 0 ? 'Cargando ítems...' : 'No hay ítems que coincidan con la búsqueda.'}
                </div>
              ) : (
                filteredCatalog.map((it) => (
                  <button
                    key={it.id_item}
                    type="button"
                    className={`registrar-stock-item-card ${selectedItem?.id_item === it.id_item ? 'selected' : ''}`}
                    onClick={() => handleSelectItem(it)}
                  >
                    <ItemImageThumb url={it.url} alt={getItemDisplayName(it)} />
                    <div className="registrar-stock-item-info">
                      <span className="registrar-stock-item-name">
                        {getItemDisplayName(it) || '-'}
                      </span>
                      {(it.sku || it.product?.sku) && (
                        <span className="registrar-stock-item-sku">SKU: {it.sku || it.product?.sku}</span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {showQuickAdd && selectedItem && (
            <div className="registrar-stock-quick-add">
              <div className="registrar-stock-quick-add-header">
                <ItemImageThumb url={selectedItem.url} alt={getItemDisplayName(selectedItem)} />
                <div>
                  <strong>{getItemDisplayName(selectedItem) || '-'}</strong>
                  {(selectedItem.sku || selectedItem.product?.sku) && (
                  <small className="d-block text-muted">SKU: {selectedItem.sku || selectedItem.product?.sku}</small>
                )}
                </div>
                <Button variant="link" className="registrar-stock-quick-add-close" onClick={closeQuickAdd} aria-label="Cerrar">
                  ×
                </Button>
              </div>
              <div className="registrar-stock-quick-add-fields">
                <div className="registrar-stock-quick-add-field">
                  <Form.Label>Cantidad</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={newQty}
                    onChange={(e) => setNewQty(e.target.value)}
                  />
                </div>
                <div className="registrar-stock-quick-add-field">
                  <Form.Label>Costo unit.</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0"
                    value={newUnitCost}
                    onChange={(e) => setNewUnitCost(e.target.value)}
                  />
                </div>
                <div className="registrar-stock-quick-add-field">
                  <Form.Label>Descuento</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={newDiscount}
                    onChange={(e) => setNewDiscount(e.target.value)}
                  />
                </div>
                <div className="registrar-stock-quick-add-field registrar-stock-quick-add-preview">
                  <Form.Label>Precio final (c/IVA)</Form.Label>
                  <div className="registrar-stock-final-price">
                    {formatterCOP.format(calcFinalPricePerUnit(newUnitCost, selectedItem?.rate || 0))}
                  </div>
                  {(selectedItem?.rate || 0) > 0 && (
                    <small className="text-muted">IVA {(parseFloat(selectedItem?.rate || 0) * 100).toFixed(1)}%</small>
                  )}
                  {parseFloat(newDiscount) > 0 && (
                    <small className="text-muted">Descuento = ganancia (no afecta precio)</small>
                  )}
                </div>
                <div className="registrar-stock-quick-add-field registrar-stock-quick-add-btn-wrap">
                  <Form.Label>&nbsp;</Form.Label>
                  <Button type="button" className="registrar-stock-btn-add" onClick={addItem}>
                    <i className="fas fa-plus me-1" /> Agregar
                  </Button>
                </div>
              </div>
            </div>
          )}

          {items.length > 0 && (
            <>
              <Table size="sm" className="registrar-stock-table">
                <thead>
                  <tr>
                    <th style={{ width: 56 }}></th>
                    <th>Ítem</th>
                    <th>Cant.</th>
                    <th>Costo unit.</th>
                    <th>Descuento</th>
                    <th>Precio final (c/IVA)</th>
                    <th>Total línea</th>
                    <th style={{ width: 44 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const lineTotal = it.price != null ? it.quantity * it.price : it.quantity * it.unit_cost * (1 + (it.rate || 0));
                    return (
                      <tr key={it.key}>
                        <td className="text-center">
                          <ItemImageThumb url={it._display?.url} alt={getItemDisplayName(it._display)} />
                        </td>
                        <td>
                          <div className="registrar-stock-table-item-name">
                            {getItemDisplayName(it._display) || it._display?.sku || it.id_item}
                          </div>
                          {(it._display?.sku || it._display?.product?.sku) && (
                            <small className="text-muted">SKU: {it._display.sku || it._display.product?.sku}</small>
                          )}
                        </td>
                        <td>{it.quantity}</td>
                        <td>{formatterCOP.format(it.unit_cost)}</td>
                        <td>{formatterCOP.format(it.discount_amount || 0)}</td>
                        <td className="registrar-stock-price-cell">
                          {formatterCOP.format(it.price ?? 0)}
                          {(it.rate || 0) > 0 && (
                            <small className="d-block text-muted">IVA {(parseFloat(it.rate || 0) * 100).toFixed(0)}%</small>
                          )}
                        </td>
                        <td>{formatterCOP.format(lineTotal)}</td>
                        <td>
                          <Button type="button" variant="link" className="registrar-stock-btn-remove" onClick={() => removeItem(it.key)} aria-label="Quitar">
                            ×
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <div className="registrar-stock-actions">
                <Button type="button" variant="outline-secondary" onClick={handleClose} disabled={loading}>
                  Cancelar
                </Button>
                <Button type="submit" className="registrar-stock-btn-save" disabled={loading}>
                  {loading ? <Spinner animation="border" size="sm" /> : 'Guardar'}
                </Button>
              </div>
            </>
          )}
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default RegistrarStockModal;
